import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { Avatar } from '../../atoms/Avatar/Avatar.js';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING } from '../../lib/focus-ring.js';
/**
 * A student, as a row in the tutor's list: avatar, name, then one quiet line of
 * context. Forwards its ref for the same reason PracticeCard does.
 *
 * Flat, with no border of its own: the parent list owns the dividers between
 * students (`divide-y divide-border`). The handoff drew this as a bordered card;
 * the students page settled on divided rows, and the card form had no screen
 * left using it.
 *
 * ── Why the card is not the link ─────────────────────────────────────────
 *
 * It used to be: one `<a>` with everything inside it, `trailing` included.
 * That is fine for a badge and wrong for anything a person can press. A
 * `<button>` inside an `<a>` is invalid — interactive content nested in
 * interactive content — and in practice pressing it would also navigate, so
 * the student's invitation had to live in a second card below this one.
 *
 * So the card is a container and the link is the part of it that reads as a
 * student: the avatar, the name and the detail line. `trailing` sits outside
 * that anchor and inside the card, which is what makes an action in it work.
 * The ref and every anchor prop still go to the `<a>`, so `createLink` wraps
 * this exactly as it did.
 *
 * The hover tint is on the container rather than the anchor, so the whole row
 * responds as one object; the focus ring stays on the anchor, because that is
 * the thing that takes focus.
 */
export const StudentCard = forwardRef(function StudentCard({ name, meta, detail, trailing, className, ...props }, ref) {
    return (_jsxs("div", { className: cn('group flex min-w-0 flex-wrap items-center gap-x-4 gap-y-3 py-4 transition-colors hover:bg-surface', className), children: [_jsxs("a", { ref: ref, className: cn('flex min-w-0 flex-1 items-center gap-4 rounded-sm', FOCUS_RING), ...props, children: [_jsx(Avatar, { name: name, size: "md" }), _jsxs("div", { className: "flex min-w-0 flex-col gap-1", children: [_jsxs("h3", { className: "min-w-0 text-h4 font-semibold text-text wrap-anywhere group-hover:text-primary-dark", children: [name, meta ? _jsxs("span", { className: "text-text-secondary", children: [" \u00B7 ", meta] }) : null] }), _jsx("p", { className: "text-body-sm text-text-secondary", children: detail })] })] }), trailing ? (_jsx("div", { className: "flex w-full min-w-0 items-center justify-start pl-14 sm:w-auto sm:justify-end sm:pl-0", children: trailing })) : null] }));
});
//# sourceMappingURL=StudentCard.js.map